import { getCalendarLabelByMode } from '@modules/common/components/DateRangePicker/CalendarPicker/helpers';
import {
  formatDateRange,
  getDateRange,
} from '@modules/common/components/DateRangePicker/helpers';
import { mapToDateTimeRange } from '@modules/common/utils';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getPreviousTimePeriodDateRangeString } from '@modules/quantify/utils';
import pluralize from 'pluralize';

interface UseGetFormattedDurationFieldLabelsProps {
  shouldShowForAllDurationTypes?: boolean;
}

export const useGetFormattedDurationFieldLabels = ({
  shouldShowForAllDurationTypes = false,
}: UseGetFormattedDurationFieldLabelsProps = {}) => {
  const duration = useQuantifyStore(({ query }) => query.duration);
  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);

  const [from, to] = getDateRange(duration);
  const primaryLabel = getCalendarLabelByMode([from, to], duration, '--');
  const durationDateRangeString = from && to ? formatDateRange(from, to) : '--';
  const secondaryLabel =
    duration.type === 'last' || shouldShowForAllDurationTypes
      ? durationDateRangeString
      : null;

  if (compareTo?.timerange || compareTo?.previousPeriod) {
    const previousPeriodLabel = compareTo?.previousPeriod
      ? `Previous ${compareTo?.previousPeriod.value} ${pluralize(
          compareTo.previousPeriod.unit,
          compareTo.previousPeriod.value,
        )}`
      : '--';

    const [fromCompare, toCompare] = compareTo.timerange
      ? getDateRange(mapToDateTimeRange(compareTo.timerange))
      : [null, null];

    const comparePrimaryLabel = compareTo?.previousPeriod
      ? previousPeriodLabel
      : compareTo.timerange
      ? getCalendarLabelByMode(
          [fromCompare, toCompare],
          mapToDateTimeRange(compareTo.timerange),
          '--',
        )
      : '--';

    const durationBPreviousPeriodString = compareTo?.previousPeriod
      ? getPreviousTimePeriodDateRangeString({
          compareTo,
          duration,
        })
      : '--';

    const durationBCustomDateRangeString =
      compareTo?.timerange && fromCompare && toCompare
        ? formatDateRange(fromCompare, toCompare)
        : '--';

    const compareSecondaryLabel = compareTo?.previousPeriod
      ? durationBPreviousPeriodString
      : !!compareTo.timerange?.relative || shouldShowForAllDurationTypes
      ? durationBCustomDateRangeString
      : null;

    return {
      durationLabel: {
        primary: primaryLabel,
        secondary: secondaryLabel,
      },
      compareDurationLabel: {
        primary: comparePrimaryLabel,
        secondary: compareSecondaryLabel,
      },
    };
  }

  return {
    durationLabel: {
      primary: primaryLabel,
      secondary: secondaryLabel,
    },
    compareDurationLabel: {
      primary: null,
      secondary: null,
    },
  };
};
